import { Grid } from '@mui/material';
import AuthService from 'utils/AuthService';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import React, {
  ChangeEvent,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useFetcher, useOutletContext } from 'react-router';
import { useBackButton } from 'hooks/useBackButton';
import { ROUTES, routeWithParams } from 'router';
import {
  Contact,
  Location,
  PhoneType,
  Property,
  PropertyType,
  Status,
  TimeZone,
} from 'types';
import { STATES_AND_PROVINCES, TIME_ZONES } from 'utils';

import AddressAutocomplete from 'components/autocompletes/AddressAutocomplete';
import ContactAutocomplete from 'components/autocompletes/ContactAutocomplete';
import TimeZoneSelect from 'components/inputs/TimeZoneSelect';
import AuturaTextField from 'components/inputs/AuturaTextField';
import { DEFAULT_BRANDING } from 'context/themeContext';
import { useTranslation } from 'react-i18next';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import { ValidatorForm } from 'react-material-ui-form-validator';

import FormFrame from 'components/layout/FormFrame';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import SettingsSection from 'components/layout/SettingsSection';
import DetailsHeader from 'components/layout/DetailsHeader';
import AuturaStateField from 'components/inputs/AuturaStateField';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import { PropertyPanelContext } from 'pages/properties/PropertyPanel';
import { EntityPanelActionHandler } from 'types/PanelRootContext';

const PropertyCreate = (): ReactNode => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(AppContext);
  const { onBack, fetcherKey, properties, displayMode, entity } =
    useOutletContext<PropertyPanelContext>();
  const fetcher = useFetcher({ key: fetcherKey });

  const [contact, setContact] = useState<Contact | undefined>();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [type, setType] = useState<PropertyType>(PropertyType.Residential);
  const [externalID, setExternalID] = useState<string>('');

  const [timeZone, setTimeZone] = useState<TimeZone>(
    TIME_ZONES.find(
      (timeZone: TimeZone) =>
        timeZone.label === state.tenant.data?.timeZone ||
        timeZone.timeZone === state.tenant.data?.timeZone,
    ) || TIME_ZONES[5], // default to America/New_York
  );

  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactPhoneType, setContactPhoneType] = useState<PhoneType>(
    PhoneType.None,
  );
  const [contactPhone, setContactPhone] = useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [contactDescription, setContactDescription] = useState<string>('');

  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [stateCode, setStateCode] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');

  // handle the browser back button
  useBackButton(onBack);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: t('buttons.create', {
        name: t('property.label', { ns: 'entities' }),
      }),
    });
  }, []);

  const handleSetLocation = (location: Location) => {
    const stateCode = STATES_AND_PROVINCES.find(
      (s) => s.stateCode === location.stateCode,
    );
    setStateCode(stateCode?.stateCode || '');
    if (location.street) {
      setStreet(location.street);
    }
    if (location.city) {
      setCity(location.city);
    }
    if (location.postalCode) {
      setPostalCode(location.postalCode);
    }
  };
  const handleSetContact = (contact: Contact | null) => {
    if (!contact) return;
    setContact(contact);
    setContactName(contact.name);
    setContactDescription(contact?.description || '');
    setContactEmail(contact?.primaryEmail || '');
    setContactPhone(contact?.primaryPhone || '');
    setContactPhoneType(contact?.primaryPhoneType || PhoneType.Day);
  };
  const handleSubmit = () => {
    const data: Property = {
      id: 0,
      tenantID: state.tenant.id,
      name: name,
      description: description,
      status: Status.Active,
      type: type,
      assignDefaultRule: true,
      users: [],
      rules: [],
      rulesets: [],
      permitTypes: [],
      qrCodes: [],
      contactID: contact?.id || 0,
      contacts: contactName
        ? [
            {
              id: contact?.id || 0,
              name: contactName,
              tenantID: state.tenant.id,
              status: contact?.status || Status.Active,
              description: contactDescription,
              primaryEmail: contactEmail,
              primaryPhone: contactPhone,
              primaryPhoneType: contactPhoneType,
              location: {},
            },
          ]
        : [],
      location: {
        id: 0,
        street: street,
        city: city,
        stateCode: stateCode,
        postalCode: postalCode,
        country:
          STATES_AND_PROVINCES.find((s) => s.stateCode === stateCode)
            ?.countryCode || '',
      },
      externalID: externalID,
      data: {
        timeZone: timeZone.timeZone,
        filterLabels: [],
        branding: DEFAULT_BRANDING,
        settings: {},
      },
    };
    if (displayMode === 'tenant') {
      fetcher.submit(
        {
          handler: EntityPanelActionHandler.TENANT_CREATE,
          body: JSON.stringify(data),
        },
        {
          method: 'POST',
          action: routeWithParams(ROUTES.PROPERTIES, [state.tenant.id]),
          encType: 'application/json',
        },
      );
    }
    if (displayMode === 'group') {
      fetcher.submit(
        {
          handler: EntityPanelActionHandler.GROUP_CREATE,
          body: JSON.stringify([data, ...properties]),
          groupID: entity.id,
        },
        {
          method: 'PUT',
          action: routeWithParams(ROUTES.PROPERTY_GROUP_PROPERTIES, [
            state.tenant.id,
            entity.id,
          ]),
          encType: 'application/json',
        },
      );
    }
  };
  const handleCancelAction = () => {
    onBack();
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    switch (name) {
      case 'name':
        setName(value);
        break;
      case 'description':
        setDescription(value);
        break;
      case 'externalID':
        setExternalID(value);
        break;
      case 'contactName':
        setContactName(value);
        break;
      case 'contactEmail':
        setContactEmail(value);
        break;
      case 'contactPhone':
        setContactPhone(value);
        break;
      case 'contactDescription':
        setContactDescription(value);
        break;
      case 'contactPhoneType':
        setContactPhoneType(value as PhoneType);
        break;
      case 'street':
        setStreet(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'postalCode':
        setPostalCode(value);
        break;
    }
  };
  const handleSetTimeZone = (timeZone: TimeZone) => {
    setTimeZone(timeZone);
  };
  const handleSetPropertyType = (e: ChangeEvent<HTMLInputElement>) => {
    setType(e.target.value as PropertyType);
  };

  return (
    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={t('buttons.create', {
            name: t('property.label', { ns: 'entities' }),
          })}
          onCancel={handleCancelAction}
          submitLabel={t('buttons.create', {
            name: t('property.label', { ns: 'entities' }),
          })}
          disableSubmit={fetcher.state !== 'idle'}
          readonly={!state.entityPermissions.property.create}
        />
        <ScrollableFormContainer maxHeight='calc(100dvh - 326px)'>
          <SettingsSection title={t('details')}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  name='name'
                  label={t('property.name', { ns: 'entities' })}
                  value={name}
                  onChange={handleChange}
                  required
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  name='description'
                  label={t('property.description', { ns: 'entities' })}
                  value={description}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaDropdown
                  label={t('property.type', { ns: 'entities' })}
                  required
                  name='type'
                  options={[
                    PropertyType.Residential,
                    PropertyType.Commercial,
                    PropertyType.Apartments,
                  ]}
                  value={type}
                  setValue={handleSetPropertyType}
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <TimeZoneSelect
                  name='timeZone'
                  fullWidth
                  inputLabel={t('property.data.timeZone', { ns: 'entities' })}
                  value={timeZone}
                  setValue={handleSetTimeZone}
                  required
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='externalID'
                  label={t('property.externalID', { ns: 'entities' })}
                  value={externalID}
                  onChange={handleChange}
                  readonly={!AuthService.IsSuperAdmin()}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaDropdown
                  name='status'
                  label={t('property.status', { ns: 'entities' })}
                  value={Status.Active}
                  readonly={!state.entityPermissions.property.create}
                  disabled
                  options={[Status.Active]}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('property.location.label', { ns: 'entities' })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid size={12} sx={{ mb: 2 }}>
                <AddressAutocomplete
                  label={t('labels.lookup_location', { ns: 'properties' })}
                  setLocation={handleSetLocation}
                  margin={'none'}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='street'
                  label={t('property.location.street', { ns: 'entities' })}
                  value={street}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property.create}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='city'
                  label={t('property.location.city', { ns: 'entities' })}
                  value={city}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property.create}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaStateField
                  stateCode={stateCode}
                  setStateCode={(stateCode: string) => {
                    setStateCode(stateCode);
                  }}
                  label={t('property.location.stateCode', { ns: 'entities' })}
                  required
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='postalCode'
                  label={t('property.location.postalCode', { ns: 'entities' })}
                  value={postalCode}
                  onChange={handleChange}
                  required
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('property.contact.label', { ns: 'entities' })}
            divider={false}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid size={12} sx={{ mb: 2 }}>
                <ContactAutocomplete
                  margin={'none'}
                  tenantID={state.tenant.id}
                  variant={'outlined'}
                  label={t('labels.add_contact', { ns: 'properties' })}
                  setContact={handleSetContact}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='contactName'
                  label={t('property.contact.name', { ns: 'entities' })}
                  value={contactName}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='contactDescription'
                  label={t('property.contact.description', { ns: 'entities' })}
                  value={contactDescription}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  name='contactPhone'
                  label={t('property.contact.primaryPhone', { ns: 'entities' })}
                  phoneNumber={contactPhone}
                  setPhoneNumber={(phone: string) => {
                    setContactPhone(phone);
                  }}
                  typeFieldName={'contactPhoneType'}
                  typeFieldLabel={t('property.contact.primaryPhoneType')}
                  phoneType={contactPhoneType}
                  setPhoneType={(phoneType: PhoneType) => {
                    setContactPhoneType(phoneType);
                  }}
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='contactEmail'
                  label={t('property.contact.primaryEmail', { ns: 'entities' })}
                  value={contactEmail}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property.create}
                />
              </Grid>
            </Grid>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

export default PropertyCreate;
